import React, { useCallback } from 'react';
import { useHistory, useParams } from 'react-router';

// Constants
import PageUrls from 'Shared/constants/PageUrls.constants';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useUpdateAiReport, handleUpdateAiReportProps } from 'Shared/utils/hooks/use-update-ai-report';

// Components
import { WpPageLayout, WpPageHeader, WpPageBody } from 'Wpds/components/page-layout';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import ShareReportModal from 'Dashboard/components/UpdateAiPage/ShareReportModal/ShareReportModal';
import ItemDoesntExistError from 'Shared/components/ItemDoesntExistError/ItemDoesntExistError';
import LoadingShimmer from 'Shared/components/LoadingShimmer/LoadingShimmer';
import ReportDropdown from 'Dashboard/components/UpdateAiPage/ReportDropdown/ReportDropdown';
import UpdateAiReport from './UpdateAiReport';
import TypingDots from 'Shared/components/TypingDots/TypingDots';

// Styles
import './UpdateAiReportPage.scss';

const UpdateAiReportPage = () => {
  const { slug } = useParams();
  const history = useHistory();

  const { report, isFetching, isUpdating, handleUpdateAiReport } = useUpdateAiReport({ slug, withFetch: true });

  const [isEditing, toggleIsEditing] = useToggle(false);
  const [isShareModalOpen, toggleIsShareModalOpen] = useToggle(false);

  const handleCloseClick = useCallback(() => {
    history.push(PageUrls.UPDATE_AI_PAGE);
  }, []);

  const handleHomeClick = () => {
    history.push(PageUrls.HOME_PAGE);
  };

  const handleUpdate = (data: handleUpdateAiReportProps) => {
    handleUpdateAiReport(data);
  };

  const baseClassName = 'UpdateAiReportPage';

  return (
    <>
      <WpPageLayout className={baseClassName} width="container">
        <WpPageHeader slim sticky>
          <div className={`${baseClassName}__header-content`}>
            {isEditing ? (
              <WpButtonGroup>
                {isUpdating ? (
                  <div className={`${baseClassName}__saving`}>
                    <TypingDots />
                    <span className={`${baseClassName}__saving__msg`}>saving</span>
                  </div>
                ) : (
                  <div className={`${baseClassName}__saving`}>
                    <WpIcon name="check" />
                    <span className={`${baseClassName}__saving__msg`}>saved</span>
                  </div>
                )}
                <WpButton onClick={toggleIsEditing} variant="secondary">
                  Done Editing
                </WpButton>
              </WpButtonGroup>
            ) : (
              <WpButtonGroup>
                {report && <ReportDropdown report={report} handleEditClick={toggleIsEditing} />}
                {report && (
                  <WpButton variant="secondary" onClick={toggleIsShareModalOpen}>
                    <WpIcon name="share" />
                    Share
                  </WpButton>
                )}
                <WpButton variant="tertiary" onClick={handleCloseClick}>
                  <WpIcon name="times" />
                </WpButton>
              </WpButtonGroup>
            )}
          </div>
        </WpPageHeader>
        <WpPageBody>
          {!isFetching && !report ? (
            <ItemDoesntExistError
              title="We couldn't find this update"
              description="You either no longer have access to it, or it has been deleted."
              buttonCallback={handleHomeClick}
              buttonLabel="Take me home"
            />
          ) : (
            <WpContainer>
              <WpContainerBody padding={24}>
                {!report || isFetching ? (
                  <div className={`${baseClassName}__loading`}>
                    <div className={`${baseClassName}__loading__section`}>
                      <LoadingShimmer shape="rectangle" width="200px" height="24px" />
                      <LoadingShimmer shape="rectangle" width="60%" height="18px" />
                      <LoadingShimmer shape="rectangle" width="55%" height="18px" />
                      <LoadingShimmer shape="rectangle" width="50%" height="18px" />
                    </div>
                    <div className={`${baseClassName}__loading__section`}>
                      <LoadingShimmer shape="rectangle" width="200px" height="24px" />
                      <LoadingShimmer shape="rectangle" width="60%" height="18px" />
                      <LoadingShimmer shape="rectangle" width="55%" height="18px" />
                      <LoadingShimmer shape="rectangle" width="50%" height="18px" />
                    </div>
                    <div className={`${baseClassName}__loading__section`}>
                      <LoadingShimmer shape="rectangle" width="200px" height="24px" />
                      <LoadingShimmer shape="rectangle" width="60%" height="18px" />
                      <LoadingShimmer shape="rectangle" width="55%" height="18px" />
                      <LoadingShimmer shape="rectangle" width="50%" height="18px" />
                    </div>
                  </div>
                ) : (
                  <UpdateAiReport report={report} isEditing={isEditing} handleUpdate={handleUpdate} />
                )}
              </WpContainerBody>
            </WpContainer>
          )}
        </WpPageBody>
      </WpPageLayout>
      <ShareReportModal slug={report?.slug} isOpen={isShareModalOpen} toggleIsOpen={toggleIsShareModalOpen} />
    </>
  );
};

export default UpdateAiReportPage;
